import React, { useCallback, useMemo, useState } from 'react';

import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/system';
import { GridToolbarColumnsButton, GridToolbarDensitySelector } from '@mui/x-data-grid';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Tooltip as ChartTooltip,
  Legend,
  LinearScale,
  Title,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  every,
  filter,
  find,
  first,
  flatMap,
  forEach,
  fromPairs,
  isEmpty,
  isNumber,
  isPlainObject,
  join,
  keys,
  map,
  max,
  size,
  sum,
  truncate,
  values,
} from 'es-toolkit/compat';
import {
  Annotation,
  AnnotationAssignment,
  AnnotationWorkingStates,
  AnnotationWorkingStateValues,
  TodoOption,
} from 'interfaces/annotation';
import { StainType } from 'interfaces/stainType';
import { Tagger } from 'interfaces/tagger';
import { Bar } from 'react-chartjs-2';
import {
  CLASS_ID_MARKER_NEG_SUFFIX,
  CLASS_ID_MARKER_POS_SUFFIX,
  CLASS_ID_MARKER_VALUE_SEPARATOR,
  CLASS_ID_SEPARATOR,
  getAnnotationsClassDisplayName,
  getTodoClassKey,
} from 'services/annotations/helpers';
import useTaxonomy from 'utils/queryHooks/taxonomy/useTaxonomy';
import { useStainTypeOptions } from 'utils/queryHooks/uiConstantsHooks';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';

import {
  ANNOTATION_ASSIGNMENT_DISPLAY_STATUS_LABELS,
  ANNOTATION_ASSIGNMENT_DISPLAY_STATUS_OPTIONS,
  ANNOTATION_ASSIGNMENT_DONE_ONLY_DISPLAY_STATUSES,
  ANNOTATION_ASSIGNMENT_IN_PROGRESS_DISPLAY_STATUSES,
  AnnotationAssignmentDisplayStatus,
  getTotalAnnotationsCount,
  matchesDisplayStatusShortcut,
} from './AnnotationAssignmentTable.utils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend, ChartDataLabels);

export interface AnnotationAssignmentStatisticsProps {
  annotationAssignment: AnnotationAssignment;
  showOverviewControls?: boolean;
  showClassGroups?: boolean;
  visibleSlideCount?: number;
  totalSlideCount?: number;
}

export interface AnnotationAssignmentToolbarProps {
  annotationAssignment: AnnotationAssignment;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  mineOnly: boolean;
  onMineOnlyChange: (nextValue: boolean) => void;
  selectedTaggerIds: number[];
  onSelectedTaggerIdsChange: (nextValue: number[]) => void;
  selectedStatuses: AnnotationAssignmentDisplayStatus[];
  onSelectedStatusesChange: (nextValue: AnnotationAssignmentDisplayStatus[]) => void;
  availableTaggers: Tagger[];
  totalRowsCount: number;
  filteredRowsCount: number;
  hasActiveFilters?: boolean;
  onClearFilters: () => void;
  selectedRows: Annotation[];
  canAnnotateSlides: boolean;
  isBulkActionPending: boolean;
  pendingTarget: AnnotationWorkingStates | null;
  onBulkWorkingStateChange: (targetWorkingState: AnnotationWorkingStates) => Promise<void>;
}

export type AnnotationClassSummary = {
  classKey: string;
  count: number;
  label: string;
  todoColor?: string;
};

const normalizeClassName = (name?: string) => (name || '').trim().toLocaleLowerCase();

const getBaseClassKey = (classKey: string) => classKey.split(CLASS_ID_SEPARATOR)[0];

// The SQL-computed diagnosis_summary always appends CLASS_ID_SEPARATOR even when there is no
// marker positivity (e.g. "roi__" instead of "roi"), unlike getTodoClassKey which omits it.
// Strip empty marker segments so both sides compare as the same canonical class key.
const canonicalizeClassKey = (classKey: string) => {
  const [diagnosis, ...markerSegments] = classKey.split(CLASS_ID_SEPARATOR);
  const nonEmptyMarkerSegments = filter(markerSegments, (segment) => segment !== '');
  return isEmpty(nonEmptyMarkerSegments)
    ? diagnosis
    : `${diagnosis}${CLASS_ID_SEPARATOR}${join(nonEmptyMarkerSegments, CLASS_ID_SEPARATOR)}`;
};

const resolveConfiguredClassKey = (annotationAssignment: AnnotationAssignment, classKey: string) => {
  const activeTodoOptions = filter(first(annotationAssignment?.todos)?.options ?? [], (option) => !option.deletedAt);
  const normalizedClassKey = normalizeClassName(canonicalizeClassKey(classKey));
  const exactOption = find(
    activeTodoOptions,
    (option) => normalizeClassName(canonicalizeClassKey(getTodoClassKey(option))) === normalizedClassKey
  );
  if (exactOption) {
    return getTodoClassKey(exactOption);
  }

  const matchingNameOption = find(
    activeTodoOptions,
    (option) => normalizeClassName(option.name) === normalizeClassName(getBaseClassKey(classKey))
  );
  return matchingNameOption ? getTodoClassKey(matchingNameOption) : classKey;
};

export const createAnnotationSummaryPerClass = (
  annotationAssignment: AnnotationAssignment,
  annotationSummaryKey: 'annotationSummary' | 'draftAnnotationSummary'
) => {
  const todoOptions = filter(first(annotationAssignment?.todos)?.options ?? [], (option) => !option.deletedAt);
  const result: Record<string, number> = {};

  forEach(todoOptions, (todoOption) => {
    if (!todoOption.isMarkerAnnotation) {
      result[getTodoClassKey(todoOption)] = 0;
    }
  });

  forEach(annotationAssignment?.annotations, (annotation) => {
    forEach(annotation[annotationSummaryKey], (count, todoId) => {
      if (isPlainObject(count) && !isNumber(count)) {
        const markerCount = count as { positive?: number; negative?: number };
        const posId = `${todoId}${CLASS_ID_MARKER_VALUE_SEPARATOR}${CLASS_ID_MARKER_POS_SUFFIX}`;
        result[posId] = (result[posId] ?? 0) + (markerCount.positive || 0);
        const negId = `${todoId}${CLASS_ID_MARKER_VALUE_SEPARATOR}${CLASS_ID_MARKER_NEG_SUFFIX}`;
        result[negId] = (result[negId] ?? 0) + (markerCount.negative || 0);
        return;
      }

      if (isNumber(count)) {
        const resolvedClassKey = resolveConfiguredClassKey(annotationAssignment, todoId);
        result[resolvedClassKey] = (result[resolvedClassKey] ?? DEFAULT_ANNOTATION_COUNT_FALLBACK) + count;
      }
    });
  });

  return result;
};

export const getSlideCountTitle = (visibleSlideCount: number, totalSlideCount: number) => {
  const slideLabel = totalSlideCount === 1 ? 'slide' : 'slides';
  return visibleSlideCount === totalSlideCount ? slideLabel : `of ${totalSlideCount} ${slideLabel}`;
};

export const AnnotationAssignmentBulkWorkingStateActions: React.FC<{
  selectedRows: Annotation[];
  isPending: boolean;
  pendingTarget: AnnotationWorkingStates | null;
  onWorkingStateChange: (targetWorkingState: AnnotationWorkingStates) => Promise<void>;
}> = ({ selectedRows, isPending, pendingTarget, onWorkingStateChange }) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const hasSelection = selectedRows.length > 0;
  const buttonLabel = pendingTarget
    ? `Changing to ${ANNOTATION_ASSIGNMENT_DISPLAY_STATUS_LABELS[pendingTarget]}…`
    : hasSelection
    ? `Change status (${selectedRows.length})`
    : 'Change status';

  const closeMenu = () => setMenuAnchor(null);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={hasSelection ? '' : 'Select slides to change their status'}>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <Button
            size="small"
            variant="outlined"
            disabled={!hasSelection || isPending}
            endIcon={<ArrowDropDownRoundedIcon />}
            aria-label="Change selected slides status"
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{ width: 176, justifyContent: 'space-between', whiteSpace: 'nowrap' }}
          >
            {buttonLabel}
          </Button>
        </Box>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {map(AnnotationWorkingStateValues, (workingState) => (
          <MenuItem
            key={workingState}
            disabled={hasSelection && every(selectedRows, (annotation) => annotation.workingState === workingState)}
            onClick={() => {
              closeMenu();
              void onWorkingStateChange(workingState);
            }}
          >
            Mark as {ANNOTATION_ASSIGNMENT_DISPLAY_STATUS_LABELS[workingState]}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export const AnnotationAssignmentStatistics: React.FC<AnnotationAssignmentStatisticsProps> = ({
  annotationAssignment,
  showOverviewControls = true,
  showClassGroups = true,
  visibleSlideCount,
  totalSlideCount,
}) => {
  const theme = useTheme();
  const { idToDisplayNameMap: taxonomyIdToDisplayNameMap } = useTaxonomy();
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();
  const { stainTypeOptions } = useStainTypeOptions();
  const resolvedTotalSlideCount = totalSlideCount ?? annotationAssignment?.annotations?.length ?? 0;
  const resolvedVisibleSlideCount = visibleSlideCount ?? resolvedTotalSlideCount;
  const slideCountTitle = getSlideCountTitle(resolvedVisibleSlideCount, resolvedTotalSlideCount);

  const annotationsCount = sum(
    flatMap(annotationAssignment?.annotations, (annotation) => getTotalAnnotationsCount(annotation?.annotationSummary))
  );
  const draftAnnotationsCount = sum(
    flatMap(annotationAssignment?.annotations, (annotation) =>
      getTotalAnnotationsCount(annotation?.draftAnnotationSummary)
    )
  );
  const [barChartAnchorEl, setBarChartAnchorEl] = useState<null | HTMLElement>(null);
  const [draftBarChartAnchorEl, setDraftBarChartAnchorEl] = useState<null | HTMLElement>(null);

  const handleChartPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setBarChartAnchorEl(event.currentTarget);
  };

  const handleChartPopoverClose = () => {
    setBarChartAnchorEl(null);
  };

  const handleDraftChartPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDraftBarChartAnchorEl(event.currentTarget);
  };

  const handleDraftChartPopoverClose = () => {
    setDraftBarChartAnchorEl(null);
  };

  const chartOpen = Boolean(barChartAnchorEl);
  const draftChartOpen = Boolean(draftBarChartAnchorEl);

  const { summaryAnnotationsPerClass, summaryDraftAnnotationsPerClass } = useMemo(() => {
    return {
      summaryAnnotationsPerClass: createAnnotationSummaryPerClass(annotationAssignment, 'annotationSummary'),
      summaryDraftAnnotationsPerClass: createAnnotationSummaryPerClass(annotationAssignment, 'draftAnnotationSummary'),
    };
  }, [annotationAssignment]);

  const getLabels = useCallback(
    (labelsKeys: string[]): string[] =>
      map(labelsKeys, (todoId) =>
        getAnnotationsClassDisplayName(todoId, taxonomyIdToDisplayNameMap, stainTypeIdToDisplayName, labelsKeys)
      ),
    [stainTypeIdToDisplayName, taxonomyIdToDisplayNameMap]
  );

  const annotationClassSummaries = useMemo<AnnotationClassSummary[]>(() => {
    const todoColorByKey = createTodoColorByKey(annotationAssignment);
    const classKeys = keys(summaryAnnotationsPerClass);
    const classLabels = getLabels(classKeys);

    return map(classKeys, (classKey, index) => ({
      classKey,
      count: summaryAnnotationsPerClass[classKey] ?? 0,
      label: classLabels[index],
      todoColor: todoColorByKey[classKey] || todoColorByKey[getBaseClassKey(classKey)],
    }));
  }, [annotationAssignment, getLabels, summaryAnnotationsPerClass]);

  const annotationClassGroups = useMemo(
    () =>
      getAnnotationClassGroups({
        annotationAssignment,
        stainTypeOptions,
        classSummaries: annotationClassSummaries,
      }),
    [annotationAssignment, annotationClassSummaries, stainTypeOptions]
  );

  const draftAnnotationClassSummaries = useMemo(() => {
    const todoColorByKey = createTodoColorByKey(annotationAssignment);
    const classKeys = keys(summaryDraftAnnotationsPerClass);
    const classLabels = getLabels(classKeys);

    return map(classKeys, (classKey, index) => ({
      classKey,
      count: summaryDraftAnnotationsPerClass[classKey] ?? 0,
      label: classLabels[index],
      todoColor: todoColorByKey[classKey] || todoColorByKey[getBaseClassKey(classKey)],
    }));
  }, [annotationAssignment, getLabels, summaryDraftAnnotationsPerClass]);

  const data = useMemo(
    () => ({
      labels: map(annotationClassSummaries, 'label'),
      datasets: [
        {
          label: 'Annotations',
          data: map(annotationClassSummaries, 'count'),
          backgroundColor: map(annotationClassSummaries, ({ todoColor }) => todoColor || theme.palette.primary.main),
          borderColor: map(annotationClassSummaries, ({ todoColor }) => todoColor || theme.palette.primary.main),
          borderWidth: 1,
        },
      ],
    }),
    [annotationClassSummaries, theme]
  );

  const options = useMemo(
    () => ({
      ...commonOptions,
      scales: {
        ...commonOptions.scales,
        x: {
          ticks: {
            color: theme.palette.text.secondary,
          },
          grid: {
            color: theme.palette.divider,
          },
        },
        y: {
          ...commonOptions.scales.y,
          ticks: {
            ...commonOptions.scales.y.ticks,
            color: theme.palette.text.secondary,
          },
          grid: {
            color: theme.palette.divider,
          },
        },
      },
      plugins: {
        ...commonOptions.plugins,
        datalabels: {
          ...commonOptions.plugins.datalabels,
          color: theme.palette.text.primary,
        },
        title: {
          position: 'top' as const,
          display: true,
          text: `${annotationsCount} Annotations`,
          color: theme.palette.text.primary,
        },
      },
    }),
    [annotationsCount, theme]
  );

  const draftData = useMemo(
    () => ({
      labels: map(draftAnnotationClassSummaries, 'label'),
      datasets: [
        {
          label: 'Draft Annotations',
          data: map(draftAnnotationClassSummaries, 'count'),
          backgroundColor: map(
            draftAnnotationClassSummaries,
            ({ todoColor }) => todoColor || theme.palette.primary.main
          ),
          borderColor: map(draftAnnotationClassSummaries, ({ todoColor }) => todoColor || theme.palette.primary.main),
          borderWidth: 1,
        },
      ],
    }),
    [draftAnnotationClassSummaries, theme]
  );

  const chartHeight = size(keys(summaryDraftAnnotationsPerClass)) * ROW_HEIGHT + PADDING_HEIGHT;

  const draftOptions = useMemo(
    () => ({
      ...commonOptions,
      scales: {
        ...commonOptions.scales,
        x: {
          ticks: {
            color: theme.palette.text.secondary,
          },
          grid: {
            color: theme.palette.divider,
          },
        },
        y: {
          ...commonOptions.scales.y,
          ticks: {
            ...commonOptions.scales.y.ticks,
            color: theme.palette.text.secondary,
          },
          grid: {
            color: theme.palette.divider,
          },
        },
      },
      plugins: {
        ...commonOptions.plugins,
        datalabels: {
          ...commonOptions.plugins.datalabels,
          color: theme.palette.text.primary,
        },
        title: {
          position: 'top' as const,
          display: true,
          text: `${draftAnnotationsCount} Draft Annotations`,
          color: theme.palette.text.primary,
        },
      },
    }),
    [draftAnnotationsCount, theme]
  );

  return (
    <Box sx={{ width: '100%' }}>
      {showOverviewControls && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 1.5,
            alignItems: 'center',
            overflowX: 'auto',
            pb: 0.5,
          }}
        >
          <AssignmentStatCell title={slideCountTitle} data={resolvedVisibleSlideCount} />
          <Divider orientation="vertical" flexItem />
          <StatsChart
            data={data}
            options={options}
            title="Annotations"
            open={chartOpen}
            anchorEl={barChartAnchorEl}
            handlePopoverClose={handleChartPopoverClose}
            handlePopoverOpen={handleChartPopoverOpen}
            chartHeight={chartHeight}
          />
          <Divider orientation="vertical" flexItem />
          <StatsChart
            data={draftData}
            options={draftOptions}
            title="Draft Annotations"
            open={draftChartOpen}
            anchorEl={draftBarChartAnchorEl}
            handlePopoverClose={handleDraftChartPopoverClose}
            handlePopoverOpen={handleDraftChartPopoverOpen}
            chartHeight={chartHeight}
          />
        </Box>
      )}

      {showOverviewControls && showClassGroups && !isEmpty(annotationClassGroups) && <Divider sx={{ my: 2 }} />}

      {showClassGroups &&
        map(annotationClassGroups, ({ key, label, count, classSummaries }, index) => (
          <React.Fragment key={key}>
            {index > 0 && <Divider sx={{ my: 2 }} />}

            <Box sx={{ pb: 0.5 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pb: 1 }}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${label}: ${count}`}
                  sx={{ '& .MuiChip-label': { fontWeight: 600 } }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {map(classSummaries, ({ classKey, count: classCount, label: classLabel, todoColor }) => (
                  <Tooltip key={classKey} title={`${classLabel}: ${classCount} annotations`}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        <Box
                          component="span"
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, maxWidth: '100%' }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              flexShrink: 0,
                              backgroundColor: todoColor || theme.palette.text.secondary,
                            }}
                          />
                          <Box
                            component="span"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {classLabel}
                          </Box>
                          <Box component="span" sx={{ fontWeight: 700, flexShrink: 0 }}>
                            {classCount}
                          </Box>
                        </Box>
                      }
                      sx={{
                        maxWidth: 320,
                        '& .MuiChip-label': {
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%',
                        },
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          </React.Fragment>
        ))}
    </Box>
  );
};

const AnnotationAssignmentTableToolbar: React.FC<AnnotationAssignmentToolbarProps> = ({
  annotationAssignment,
  searchText,
  onSearchTextChange,
  mineOnly,
  onMineOnlyChange,
  selectedTaggerIds,
  onSelectedTaggerIdsChange,
  selectedStatuses,
  onSelectedStatusesChange,
  availableTaggers,
  totalRowsCount,
  filteredRowsCount,
  hasActiveFilters = false,
  onClearFilters,
  selectedRows,
  canAnnotateSlides,
  isBulkActionPending,
  pendingTarget,
  onBulkWorkingStateChange,
}) => {
  const selectedTaggerNames = useMemo(
    () =>
      map(
        filter(availableTaggers, (tagger) => selectedTaggerIds.includes(tagger.taggerId)),
        'name'
      ),
    [availableTaggers, selectedTaggerIds]
  );

  const inProgressShortcutSelected = useMemo(
    () =>
      matchesDisplayStatusShortcut({
        selectedStatuses,
        shortcutStatuses: ANNOTATION_ASSIGNMENT_IN_PROGRESS_DISPLAY_STATUSES,
      }),
    [selectedStatuses]
  );

  const doneOnlyShortcutSelected = useMemo(
    () =>
      matchesDisplayStatusShortcut({
        selectedStatuses,
        shortcutStatuses: ANNOTATION_ASSIGNMENT_DONE_ONLY_DISPLAY_STATUSES,
      }),
    [selectedStatuses]
  );

  return (
    <Box
      sx={{
        px: 0.5,
        py: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 360px', overflow: 'hidden' }}>
          <AnnotationAssignmentStatistics
            annotationAssignment={annotationAssignment}
            showOverviewControls
            showClassGroups={false}
            visibleSlideCount={filteredRowsCount}
            totalSlideCount={totalRowsCount}
          />
        </Box>

        <Box
          sx={{
            flex: '1 1 520px',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'stretch',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <TextField
              size="small"
              type="search"
              label="Search"
              placeholder="Slide, assignee, or status"
              value={searchText}
              onChange={(event) => onSearchTextChange(event.target.value)}
              sx={{ minWidth: 240, flex: '1 1 240px', maxWidth: 320 }}
            />

            <Chip
              clickable
              color={mineOnly ? 'primary' : 'default'}
              variant={mineOnly ? 'filled' : 'outlined'}
              label="My annotations"
              onClick={() => onMineOnlyChange(!mineOnly)}
            />

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select
                multiple
                displayEmpty
                value={selectedTaggerIds}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  onSelectedTaggerIdsChange(
                    typeof nextValue === 'string'
                      ? map(nextValue.split(','), (value) => Number(value))
                      : map(nextValue, (value) => Number(value))
                  );
                }}
                renderValue={(selectedValue) => {
                  const selectedNames = filter(
                    map(selectedValue as number[], (taggerId) => find(availableTaggers, { taggerId })?.name),
                    Boolean
                  );
                  return isEmpty(selectedValue) ? 'All assignees' : join(selectedNames, ', ');
                }}
                inputProps={{ 'aria-label': 'Filter assignees' }}
              >
                {map(availableTaggers, (tagger) => (
                  <MenuItem key={tagger.taggerId} value={tagger.taggerId}>
                    {tagger.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button size="small" onClick={onClearFilters} disabled={!hasActiveFilters}>
              Clear
            </Button>

            <GridToolbarColumnsButton />
            <GridToolbarDensitySelector />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Shortcuts:
              </Typography>
              <Chip
                clickable
                size="small"
                color={inProgressShortcutSelected ? 'primary' : 'default'}
                variant={inProgressShortcutSelected ? 'filled' : 'outlined'}
                label="In progress"
                onClick={() => {
                  onSelectedStatusesChange(
                    inProgressShortcutSelected ? [] : ANNOTATION_ASSIGNMENT_IN_PROGRESS_DISPLAY_STATUSES
                  );
                }}
              />
              <Chip
                clickable
                size="small"
                color={doneOnlyShortcutSelected ? 'primary' : 'default'}
                variant={doneOnlyShortcutSelected ? 'filled' : 'outlined'}
                label="Done only"
                onClick={() => {
                  onSelectedStatusesChange(
                    doneOnlyShortcutSelected ? [] : ANNOTATION_ASSIGNMENT_DONE_ONLY_DISPLAY_STATUSES
                  );
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Status:
              </Typography>
              {map(ANNOTATION_ASSIGNMENT_DISPLAY_STATUS_OPTIONS, (workingState) => {
                const selected = selectedStatuses.includes(workingState);

                return (
                  <Chip
                    key={workingState}
                    clickable
                    size="small"
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    label={ANNOTATION_ASSIGNMENT_DISPLAY_STATUS_LABELS[workingState]}
                    onClick={() => {
                      onSelectedStatusesChange(
                        selected
                          ? filter(selectedStatuses, (status) => status !== workingState)
                          : [...selectedStatuses, workingState]
                      );
                    }}
                  />
                );
              })}
              {canAnnotateSlides && (
                <AnnotationAssignmentBulkWorkingStateActions
                  selectedRows={selectedRows}
                  isPending={isBulkActionPending}
                  pendingTarget={pendingTarget}
                  onWorkingStateChange={onBulkWorkingStateChange}
                />
              )}
            </Box>
          </Box>

          {!isEmpty(selectedTaggerNames) && (
            <Typography variant="caption" color="text.secondary">
              Assignees: {join(selectedTaggerNames, ', ')}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const createTodoColorByKey = (annotationAssignment: AnnotationAssignment) => {
  const colorByKey: Record<string, string | undefined> = {};

  forEach(first(annotationAssignment?.todos)?.options, (todoOption) => {
    const todoClassKey = getTodoClassKey(todoOption);
    colorByKey[todoClassKey] = todoOption.color;

    if (!colorByKey[todoOption.name]) {
      colorByKey[todoOption.name] = todoOption.color;
    }
  });

  return colorByKey;
};

const ROI_CATEGORY_KEY = 'roi';
const CELL_CLASSIFICATION_CATEGORY_KEY = 'cell-classification';
const PHENOTYPIC_MARKER_CATEGORY_KEY = 'phenotypic-marker';
const LINEAGE_MARKER_CATEGORY_KEY = 'lineage-marker';
const OTHER_REGION_CATEGORY_KEY = 'other-region';
const OTHER_CATEGORY_KEY = 'other';
const UNCONFIGURED_CATEGORY_KEY = 'unconfigured';

const annotationsStatsCategoryKeys = [
  ROI_CATEGORY_KEY,
  CELL_CLASSIFICATION_CATEGORY_KEY,
  PHENOTYPIC_MARKER_CATEGORY_KEY,
  LINEAGE_MARKER_CATEGORY_KEY,
  OTHER_REGION_CATEGORY_KEY,
  OTHER_CATEGORY_KEY,
  UNCONFIGURED_CATEGORY_KEY,
] as const;

type AnnotationStatsCategoryKey = (typeof annotationsStatsCategoryKeys)[number];

const annotationStatsCategoryLabels: Record<AnnotationStatsCategoryKey, string> = {
  roi: 'ROIs',
  'cell-classification': 'Cell classification',
  'phenotypic-marker': 'Phenotypic markers',
  'lineage-marker': 'Lineage markers',
  'other-region': 'Other regions',
  other: 'Other annotations',
  unconfigured: 'Unconfigured annotation values',
};

export const getAnnotationClassGroups = ({
  annotationAssignment,
  stainTypeOptions,
  classSummaries,
}: {
  annotationAssignment: AnnotationAssignment;
  stainTypeOptions: StainType[];
  classSummaries: AnnotationClassSummary[];
}) => {
  const summariesByCategory: Record<AnnotationStatsCategoryKey, AnnotationClassSummary[]> = fromPairs(
    map(annotationsStatsCategoryKeys, (key) => [key, [] as AnnotationClassSummary[]])
  );

  forEach(classSummaries, (classSummary) => {
    const category = getAnnotationStatsCategory({
      annotationAssignment,
      stainTypeOptions,
      classKey: classSummary.classKey,
    });
    summariesByCategory[category].push(classSummary);
  });

  return filter(
    map(annotationStatsCategoryLabels, (label, key) => {
      const categoryKey = key as AnnotationStatsCategoryKey;
      const groupedClassSummaries = summariesByCategory[categoryKey];

      return {
        key: categoryKey,
        label,
        count: sum(map(groupedClassSummaries, 'count')),
        classSummaries: groupedClassSummaries,
      };
    }),
    ({ classSummaries: groupedClassSummaries }) => !isEmpty(groupedClassSummaries)
  );
};

const getAnnotationStatsCategory = ({
  annotationAssignment,
  stainTypeOptions,
  classKey,
}: {
  annotationAssignment: AnnotationAssignment;
  stainTypeOptions: StainType[];
  classKey: string;
}): AnnotationStatsCategoryKey => {
  const todoOption = findTodoOptionForClassKey(annotationAssignment, classKey);

  if (!todoOption) {
    return UNCONFIGURED_CATEGORY_KEY;
  }

  const normalizedName = todoOption.name.toLowerCase();
  const normalizedDisplayName = (todoOption.displayName || '').toLowerCase();
  const isRoi =
    todoOption.className === 'polygon' &&
    (normalizedName.includes(ROI_CATEGORY_KEY) || normalizedDisplayName.includes(ROI_CATEGORY_KEY));

  if (isRoi) {
    return ROI_CATEGORY_KEY;
  }

  if (todoOption.isMarkerAnnotation) {
    const stainType = find(stainTypeOptions, { id: getBaseClassKey(classKey) });

    if (stainType?.phenotypic) {
      return PHENOTYPIC_MARKER_CATEGORY_KEY;
    }

    if (stainType?.lineage) {
      return LINEAGE_MARKER_CATEGORY_KEY;
    }

    return OTHER_CATEGORY_KEY;
  }

  if (todoOption.className === 'point') {
    return CELL_CLASSIFICATION_CATEGORY_KEY;
  }

  if (todoOption.className === 'polygon') {
    return OTHER_REGION_CATEGORY_KEY;
  }

  return OTHER_CATEGORY_KEY;
};

const findTodoOptionForClassKey = (
  annotationAssignment: AnnotationAssignment,
  classKey: string
): TodoOption | undefined => {
  const todoOptions = first(annotationAssignment?.todos)?.options ?? [];

  return (
    find(
      todoOptions,
      (todoOption) => normalizeClassName(getTodoClassKey(todoOption)) === normalizeClassName(classKey)
    ) ||
    find(
      todoOptions,
      (todoOption) => normalizeClassName(todoOption.name) === normalizeClassName(getBaseClassKey(classKey))
    )
  );
};

const AssignmentStatCell: React.FC<{ title: string; data: any }> = ({ title, data }) => {
  return (
    <Grid container direction="column">
      <Grid item>
        <Typography variant="body1" fontWeight="bold">
          {data}
        </Typography>
      </Grid>
      <Grid item>
        <Typography variant="subtitle2">{title}</Typography>
      </Grid>
    </Grid>
  );
};

const StatsChart: React.FC<{
  data: any;
  options: any;
  title: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  handlePopoverClose: () => void;
  handlePopoverOpen: (event: React.MouseEvent<HTMLElement>) => void;
  chartHeight?: number;
}> = ({ data, options, title, open, anchorEl, handlePopoverClose, handlePopoverOpen, chartHeight }) => {
  const totalCount = sum(values(flatMap(data.datasets, 'data')));

  const longestLabelLength = useMemo(() => {
    return max(map(data.labels, (label: string) => size(label))) ?? 0;
  }, [data.labels]);

  return (
    <Grid container direction="row" spacing={1}>
      <Grid item>
        <IconButton
          sx={(theme) => ({
            borderRadius: '4px',
            backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[900],
          })}
          onClick={handlePopoverOpen}
        >
          <BarChartRoundedIcon />
        </IconButton>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Box sx={{ minWidth: 700, width: longestLabelLength * 20, maxWidth: 1000, maxHeight: 800 }} m={2} p={1}>
            <Bar data={data} options={options} height={chartHeight} plugins={[ChartDataLabels]} />
          </Box>
        </Popover>
      </Grid>
      <Grid item>
        <AssignmentStatCell title={title} data={totalCount} />
      </Grid>
    </Grid>
  );
};

const DEFAULT_ANNOTATION_COUNT_FALLBACK = 0;

const ROW_HEIGHT = 10;
const PADDING_HEIGHT = 100;
const commonOptions = {
  responsive: true,
  indexAxis: 'y' as const,
  layout: {
    padding: {
      right: 20,
      bottom: 20,
      left: 10,
    },
  },
  scales: {
    y: {
      ticks: {
        callback: function (
          this: { getLabelForValue: (value: number | string) => string },
          value: number | string
        ): string {
          const label = this.getLabelForValue(value);
          return truncate(label, { length: 80 });
        },
      },
    },
  },
  plugins: {
    datalabels: {
      display: true,
      color: 'inherit',
      anchor: 'end',
      align: 'end',
    },
    legend: {
      display: false,
    },
  },
};

export default AnnotationAssignmentTableToolbar;
